import React, { ComponentProps, useState } from 'react'
import { To } from 'react-router-dom'

import { Button, type Icon } from '@factorialco/deprecated-design-system'

import type Popover from 'modules/core/components/PopoverMenu'

type BaseAction = {
  label: string
  disabled?: boolean
  icon?: React.ComponentProps<typeof Icon.Medium>['icon']
  hint?: React.ReactNode
}
type HrefAction = {
  href: string
  download?: boolean | string
  target?: '_blank'

  to?: never
  type?: never
  subActions?: never
  onClick?: never
}

type RouterAction = {
  to: To
  onClick?: () => void

  href?: never
  download?: never
  target?: never
  type?: never
  subActions?: never
}

type ClickableAction = {
  onClick?: () => void

  to?: never
  href?: never
  download?: never
  target?: never
  type?: never
  subActions?: never
}
type ComposedAction = {
  type: 'multiple'
  subActions: SubAction[]
  to?: never
  href?: never
  download?: never
  target?: never
  onClick?: never
}

export type Action = BaseAction & (HrefAction | RouterAction | ClickableAction | ComposedAction)

export type SubAction = Omit<BaseAction, 'icon'> & (HrefAction | RouterAction | ClickableAction)

export type Option = {
  title: string
  description?: string
  onClick?: () => void
  to?: string
  danger?: boolean
  disabled?: boolean
} & (
  | {
      href: React.ComponentProps<typeof Popover.HrefOption>['href']
      download?: React.ComponentProps<typeof Popover.HrefOption>['download']
      target?: React.ComponentProps<typeof Popover.HrefOption>['target']
    }
  | { href?: undefined }
)

interface ButtonTypeProps {
  action: Action
  variant: ComponentProps<typeof Button.Router>['type']
}

export function ButtonType({
  action,
  children,
  variant,
}: React.PropsWithChildren<ButtonTypeProps>) {
  const [isLoading, setIsLoading] = useState(false)

  const onClick = async () => {
    const callback = 'onClick' in action ? action.onClick?.() : undefined
    if (!callback?.['then']) return

    setIsLoading(true)
    await callback
    setIsLoading(false)
  }

  const buttonProps: Partial<ComponentProps<typeof Button.Router>> = {
    type: variant,
    onClick,
    disabled: action.disabled || isLoading,
  }

  if (action.href) {
    return action.href ? (
      <Button.Router
        {...buttonProps}
        to={action.href}
        download={action.download}
        blankTarget={!!action.target}
        iconName={action.icon}
      >
        {children as string}
      </Button.Router>
    ) : (
      <Button {...buttonProps} iconName={action.icon}>
        {children as string}
      </Button>
    )
  }

  if (action.to) {
    return (
      <Button.Router {...buttonProps} to={action.to} iconName={action.icon}>
        {children as string}
      </Button.Router>
    )
  }

  return (
    <Button {...buttonProps} iconName={action.icon}>
      {children as string}
    </Button>
  )
}
